import { Model } from 'dva'
import { FailureHandler } from 'Utils/ApiHelper'
import { OrganisationsService } from '../../Service'

const initialState = {
  flow: null, // Entire flow object. Consists of versions & subflows
  version: null, // Selected versions
  subFlow: null, // Selected Subflow
  language: null, // Selected Language
  reactFlowInstance: null,
  highlightedBlock: null,
}

const Layout: Model = {
  namespace: 'flowLayout',
  state: initialState,
  effects: {
    *setFlow(
      {
        uuid,
        organisationUuid,
        version = 'latest_version',
        subFlow = 'start',
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      // Fetch Flow
      try {
        const flow = yield call(OrganisationsService.organisationsFlowsRetrieve, {
          organisationUuid,
          uuid: uuid,
        })
        yield put({ type: 'setState', payload: { flow } })
        yield put({
          type: 'setFlowVersion',
          version,
          subFlow,
          setLoading,
          onSuccess,
          onFailure,
        })
      } catch (err) {
        console.debug(`Error retrieving flow with uuid: ${uuid}`)
        console.error(err)
        onFailure()
      }
      setLoading(false)
    },
    *setFlowVersion(
      {
        version = 'latest_version',
        subFlow = 'start',
        setLoading = () => {},
        onSuccess = () => {},
        onFailure = () => {},
      },
      { call, put, select }
    ): any {
      setLoading(true)
      let flow = yield select((state: any) => state.flowLayout.flow)
      version = version === 'latest_version' ? flow.result?.latest_version : version
      // Fetch Flows
      try {
        const flowConfig = yield call(OrganisationsService.organisationsFlowsVersionsRetrieve, {
          organisationUuid: flow?.organisation?.uuid,
          flowUuid: flow.uuid,
          version: version,
        })
        yield put({ type: 'setState', payload: { version: flowConfig } })
        yield put({
          type: 'setSubFlow',
          subFlow,
          setLoading: setLoading,
          onSuccess,
          onFailure,
        })
      } catch (err) {
        FailureHandler(`Error retrieving version: ${version} for flow with uuid: ${flow.uuid}`, err)
        onFailure(flow)
      }
      setLoading(false)
    },
    *setSubFlow(
      { subFlow = 'start', setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      let flow = yield select((state: any) => state.flowLayout.flow)
      let version = yield select((state: any) => state.flowLayout.version)
      // Fetch Flows
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsSubFlowsRetrieve,
          {
            organisationUuid: flow?.organisation?.uuid,
            flowUuid: flow.uuid,
            flowConfigVersion: version.version,
            slug: subFlow,
            fields: ['id', 'name', 'slug'],
          }
        )
        yield put({ type: 'setState', payload: { subFlow: response } })
        onSuccess(flow, version, response)
      } catch (err) {
        FailureHandler(`Error retrieving version: ${version} for flow with uuid: ${flow.uuid}`, err)
        onFailure(flow, version)
      }
      setLoading(false)
    },
    *setSmsFlow(
      { uuid, organisationUuid, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put }
    ) {
      try {
        const flow = yield call(OrganisationsService.organisationsSmsFlowsRetrieve, {
          organisationUuid,
          uuid: uuid,
        })
        yield put({ type: 'setState', payload: { flow } })
      } catch (err) {
        FailureHandler(`Error retrieving flow with uuid: ${uuid}`, err)
        onFailure()
      }
      setLoading(false)
    },
  },
  reducers: {
    setState(state, action) {
      return {
        ...state,
        ...action.payload,
      }
    },
    setLanguage(state, action) {
      try {
        let ux = localStorage.getItem('user_preferences')
        if (ux !== null) {
          ux = JSON.parse(ux)
          console.info('setLanguage: ', state.flow.uuid, state.version.version)
          // @ts-expect-error : Will update later
          ux[state.flow.uuid][state.version.version].language = action.payload.code
          localStorage.setItem('user_preferences', JSON.stringify(ux))
        }
      } catch (e) {
        console.info('setLanguage Error', e)
      }
      return {
        ...state,
        language: action.payload,
      }
    },
    addVersionToSelectedFlow(state, action) {
      return {
        ...state,
        flow: {
          ...state.flow,
          result: {
            ...state.flow.result,
            latest_version: action.payload,
            versions: [action.payload, ...state.flow.result.versions],
          },
        },
      }
    },
    setReactFlowInstance(state, action) {
      const { instance } = action.payload
      if (!instance?.getElements) {
        console.error('Received invalid reactFlowInstance')
        return state
      }
      return {
        ...state,
        reactFlowInstance: instance,
      }
    },
    setHighlightedBlock(state, action) {
      const { blockID } = action.payload
      if (blockID) {
        return {
          ...state,
          highlightedBlock: blockID,
        }
      }
      return {
        ...state,
        highlightedBlock: null,
      }
    },
    updateDeployedVersion(state, action) {
      if (state?.flow?.result?.current_version) {
        state.flow.result.current_version = action.payload
        return {
          ...state,
        }
      }
      return state
    },
    updateExperimentVersion(state, action) {
      if (state?.flow?.result?.current_version) {
        return {
          ...state,
          flow: {
            ...state.flow,
            experiment_versions: [...state.flow.experiment_versions, action.payload],
          },
        }
      }
      return state
    },
    updateBetaVersion(state, action) {
      if (state?.flow?.result?.current_version) {
        state.flow.result.beta_version = action.payload
        return {
          ...state,
        }
      }
      return state
    },
    updateExperimentDetailsForApplication(state, action) {
      if (state?.flow?.result?.current_version) {
        state.flow.experiment_enabled = action.payload.experiment_enabled
        state.flow.experiment_version = action.payload.experiment_version
        state.flow.experiment_platform_config = action.payload.experiment_platform_config
        return {
          ...state,
        }
      }
      return state
    },
    resetFlowLayout(state, action) {
      return {
        ...state,
        ...initialState,
      }
    },
  },
}

export default Layout
